import { InputField } from '@clay-run/glaze/input-field';
import { RadioGroupField } from '@clay-run/glaze/radio-group-field';

import { SENDER_TOKENS } from '../../../_shared/launcher-copy-tokens';
import { STATIC_EMAIL_SENDER_OPTION, type SenderChoice, type SenderOption } from '../../sender-choice';

/**
 * Launcher V2 PR C — the two controls that pick a `mapping.sender`, used by
 * `SequencesStep.tsx`'s inline sender popover (`InlineSenderPopover`; PR C /
 * U8 — replaced the earlier `SenderModal`). Through PR C this was also shared
 * with `SequenceMappingModal` (mapping a rung to a sequence needed a sender in
 * the same breath, back when `SequenceMappingSchema.sender` was
 * required-non-empty); QA-fixes E2c (plan Decision 10) pulled sender
 * collection out of that picker entirely, so the inline popover is the only
 * caller now.
 *
 * Controlled and stateless: the parent owns the {@link SenderChoice} and the
 * validation result, so a single Save button can gate on both this and whatever
 * else that popover collects. All of the vocabulary and the format rule live in
 * the DOM-free `sender-choice.ts` — this file is layout only.
 *
 * `error` is rendered through Glaze's own `errorMessage`/`invalid` props rather
 * than a hand-rolled line of red text, matching how the rest of the play flow
 * surfaces field-level problems.
 */
export function SenderChoiceFields({
  value,
  onChange,
  error,
  label = 'Sender',
}: {
  value: SenderChoice;
  onChange: (next: SenderChoice) => void;
  /** The reason the current choice isn't a usable sender yet, or `null`. */
  error: string | null;
  /**
   * playbooks-inflection-wiring (2026-09-17) — `SequenceInflectionPopover`
   * renders TWO of these (one for CC, one for Reply-to), so the radio
   * group's own label needs to say which field it is. Defaults to `'Sender'`,
   * the only label every pre-existing caller ever passed.
   */
  label?: string;
}) {
  const isStaticEmail = value.option === STATIC_EMAIL_SENDER_OPTION;

  return (
    <>
      <RadioGroupField
        label={label}
        value={value.option}
        onValueChange={(option) => onChange({ ...value, option: option as SenderOption })}
        options={[
          ...SENDER_TOKENS.map((token) => ({ value: token.value, label: token.label })),
          { value: STATIC_EMAIL_SENDER_OPTION, label: 'Static email address' },
        ]}
      />
      {isStaticEmail && (
        <InputField
          label="Email"
          type="email"
          placeholder="email@clay.com"
          value={value.email}
          onChange={(e) => onChange({ ...value, email: e.target.value })}
          // The `type="email"` above is a keyboard/autofill hint only: this
          // input sits in no `<form>`, so the browser's native constraint
          // validation never runs. `error` (from `resolveSenderChoice`) is the
          // real check — see `sender-choice.ts`'s header.
          invalid={error !== null}
          errorMessage={error ?? undefined}
        />
      )}
    </>
  );
}
